import { backgroundColor } from "@/utils/theme";
import { useResize } from "@/utils/useResize";
import { getResponsiveTableStyles } from "@/utils/responsive-utils";
import { ErrorModal, useContextMenuMESF } from "@dexteel/mesf-core";
import { Button, Grid, Paper } from "@mui/material";
import { PlaylistAdd as PlaylistAddIcon } from "@mui/icons-material";
import {
  GetContextMenuItemsParams,
  MenuItemDef,
  RowClassParams,
  RowStyle,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useEffect, useRef, useState } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { ConfirmationDialogRaw } from "../../../../controls/modals/ConfirmationDialogRaw";
import { useHeatsOptionsFunctions } from "../hooks/useHeatsOptionsFunctions";
import { CreateHeatModal } from "./CreateModal/CreateHeatModal";
import { EditHeatModal } from "./EditModal/EditHeatModal";
import { useTableData } from "./useTableData";
import { Heat } from "../../models/Heat";
import { deleteHeat } from "../repositories/heatsRepository";
import {
  PourbackModal,
  FormData as PourbackFormData,
} from "@/pages/caster/data-entry/components/modals/pourback/PourbackModal";
import { upsertPourback, undeleteHeat } from "../repositories/heatsRepository";
import { HeatDataEntryType } from "../models/HeatDataEntryType";
import moment from "moment";
import { defaultColDef, defaultGridOptions } from "@/setup/ag-grid/mesf-theme";

type Props = {
  isLoading: boolean;
  getHeatsByShift: () => Promise<void>;
  heatsByShift: Heat[];
  heatSelected: Heat | null;
  showDeletedHeats: boolean;
};

export const HeatsByShiftTable = ({
  isLoading,
  getHeatsByShift,
  heatsByShift,
  heatSelected,
  showDeletedHeats,
}: Props) => {
  const [modalEditHeat, setModalEditHeat] = useState(false);
  const [modalCreateHeat, setModalCreateHeat] = useState(false);
  const [modalPourback, setModalPourback] = useState(false);
  const [heatIdForEdit, setHeatIdForEdit] = useState<number | null>(null);
  const [selectedHeat, setSelectedHeat] = useState<HeatDataEntryType | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const { width, height } = useResize();

  // Calculate responsive styles using shared utility
  const tableStyles = getResponsiveTableStyles(width, height);

  const handleOpenPourbackModal = (heat: HeatDataEntryType) => {
    if (heat) {
      setHeatIdForEdit(heat.HeatId);
      setSelectedHeat(heat);
      setModalPourback(true);
    }
  };

  const handleUndeleteHeat = async (heatId: number) => {
    try {
      const resp = await undeleteHeat(heatId);
      if (!resp.ok) {
        setError(resp.message || "Failed to restore heat");
        return;
      }
      await getHeatsByShift();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to restore heat");
    }
  };

  const {
    getMenuOptions,
    goToCasterDataEntry,
    goToEAFDataEntry,
    goToLMFDataEntry,
    goToLABDataEntry,
    showDeleteModal,
    messageDeleteModal,
    heatIdForDelete,
    setShowDeleteModal,
    setMessageDeleteModal,
  } = useHeatsOptionsFunctions({
    setHeatIdForEdit,
    setModalEditHeat,
    setModalCreateHeat,
    setModalPourback: handleOpenPourbackModal,
    onUndeleteHeat: handleUndeleteHeat,
  });

  const { showContextMenu, registerConfig } = useContextMenuMESF();

  const { columns } = useTableData({
    showContextMenu,
    goToCasterDataEntry,
    goToEAFDataEntry,
    goToLMFDataEntry,
    goToLABDataEntry,
  });

  const handleDeleteHeat = async (value?: string) => {
    setShowDeleteModal(false);
    setMessageDeleteModal("");
    if (value !== "OK") return;
    if (heatIdForDelete === null) return;
    setIsDeleting(true);
    const resp = await deleteHeat(heatIdForDelete);
    if (resp.ok) {
      setIsDeleting(false);
      await getHeatsByShift();
    } else {
      setIsDeleting(false);
      setError(resp.message);
    }
  };

  const handlePourbackOnSubmit = async (
    data: PourbackFormData,
    receivedPourbackId?: number | null,
  ) => {
    try {
      await upsertPourback({
        PourbackId: receivedPourbackId || null,
        HeatId: heatIdForEdit!,
        SourceProcessId:
          selectedHeat?.CASProcessId ||
          selectedHeat?.LMFProcessId ||
          selectedHeat?.EAFProcessId,
        PourbackWeight: data.PourbackWeight!,
        PourbackReasonId: data.PourbackReasonId!,
        Comments: data.PourbackComments,
        SourceTime:
          selectedHeat?.LadleOpen ||
          selectedHeat?.LMFArrive ||
          selectedHeat?.StartTap ||
          moment.utc().toDate(),
        IsFinished: data.IsFinished,
      });
    } catch (error) {
      throw new Error("Failed to submit pourback data: " + error);
    }
  };

  const getContextMenuItems = (
    params: GetContextMenuItemsParams,
  ): MenuItemDef[] => {
    const data = params.node?.data;
    // Use params.event (ag-grid internal) instead of global window.event for cross-browser support
    const nativeEvent = (
      params as GetContextMenuItemsParams & { event?: MouseEvent }
    ).event;

    if (data && nativeEvent) {
      showContextMenu(
        nativeEvent as unknown as React.MouseEvent,
        data,
        "heatsByShiftTable",
      );
    }

    const addNewHeat: MenuItemDef[] = [];

    if (!data) {
      addNewHeat.push({
        name: "New Heat",
        icon: renderToStaticMarkup(<PlaylistAddIcon />),
        action: () => setModalCreateHeat(true),
      });
    }
    return addNewHeat;
  };

  const getMenuOptionsRef = useRef(getMenuOptions);
  getMenuOptionsRef.current = getMenuOptions;

  useEffect(() => {
    registerConfig({
      id: "heatsByShiftTable",
      getOptions: (...args: [HeatDataEntryType]) =>
        getMenuOptionsRef.current(...args),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <div onContextMenu={(e) => e.preventDefault()}>
        <Grid container>
          <Grid
            item
            md={12}
            xs={12}
            style={tableStyles}
            component={Paper}
            elevation={3}
          >
            <ConfirmationDialogRaw
              title="Confirmation"
              message={messageDeleteModal}
              open={showDeleteModal}
              onClose={handleDeleteHeat}
            />
            <EditHeatModal
              heatIdForEdit={heatIdForEdit}
              show={modalEditHeat}
              onHide={(shouldUpdate: boolean) => {
                setModalEditHeat(false);
                if (shouldUpdate) getHeatsByShift().then(() => {});
              }}
              heatsByShift={heatsByShift}
            />
            <CreateHeatModal
              show={modalCreateHeat}
              onHide={(shouldUpdate: boolean) => {
                setModalCreateHeat(false);
                if (shouldUpdate) getHeatsByShift().then(() => {});
              }}
              heatsByShift={heatsByShift}
            />
            <PourbackModal
              show={modalPourback}
              onHide={(shouldUpdate) => {
                setModalPourback(false);
                setSelectedHeat(null);
                setHeatIdForEdit(null);
                if (shouldUpdate) getHeatsByShift().then(() => {});
              }}
              heat={selectedHeat}
              onSubmit={handlePourbackOnSubmit}
            />
            <AgGridReact
              loading={isLoading}
              defaultColDef={defaultColDef}
              rowData={heatsByShift}
              columnDefs={columns}
              getContextMenuItems={getContextMenuItems}
              onRowDoubleClicked={(row) => {
                setHeatIdForEdit(row.data.HeatId);
                setModalEditHeat(true);
              }}
              rowClassRules={{
                "deleted-row": (params) => !params.data?.IsActive,
              }}
              getRowStyle={(
                params: RowClassParams<any, any>,
              ): RowStyle | undefined => {
                if (params.data.HeatName === heatSelected?.HeatName) {
                  return { backgroundColor: backgroundColor };
                }
                return undefined;
              }}
              {...defaultGridOptions}
              suppressContextMenu={false}
            />
            <Grid container justifyContent="flex-end">
              <Grid
                item
                md={2}
                xs={12}
                sx={{ display: "flex", justifyContent: "flex-end" }}
              >
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  onClick={() => setModalCreateHeat(!modalCreateHeat)}
                  style={{ margin: "1.5rem 0px 0px" }}
                >
                  New Heat
                </Button>
              </Grid>
            </Grid>
            <ErrorModal error={error} onHide={() => setError("")} />
          </Grid>
        </Grid>
      </div>
    </>
  );
};
